import Reveal from "@/components/Reveal";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { CHECKLISTS } from "@/data/content";
import { useSEO } from "@/lib/useSEO";

const downloadChecklist = (c) => {
  const lines = [
    "SMART LEGAL SOLUTIONS",
    c.title.toUpperCase(),
    "",
    ...c.items.map((it, i) => `${String(i + 1).padStart(2, "0")}.  ${it}`),
    "",
    "Note: Document and procedural requirements differ according to the facts of each case",
    "and the applicable procedure. This checklist is general information only and does not",
    "constitute legal advice or an automated legal conclusion.",
    "",
    "Smart Legal Solutions · Vijayawada, Andhra Pradesh",
    "https://smartlegalsolutions.co.in",
  ].join("\n");
  const blob = new Blob([lines], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `SLS-${c.id}-checklist.txt`;
  a.click();
  URL.revokeObjectURL(url);
  toast.success("Checklist downloaded");
};

export default function Resources() {
  useSEO("Preparing for a Legal Consultation | Smart Legal Solutions", "Informational document checklists to help prepare for a legal consultation across bail, civil, property, High Court, cheque dishonour, cyber and departmental matters.");

  return (
    <div data-testid="resources-page" className="bg-warm pt-32">
      <div className="mx-auto max-w-7xl px-5 pb-24 lg:px-10">
        <Reveal>
          <p className="mono-label-dark">Resources</p>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl font-medium leading-[1.1] tracking-tight text-navy sm:text-5xl lg:text-6xl">
            Preparing for a Legal Consultation
          </h1>
          <div className="gold-hairline mt-8" />
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-navy/60">
            Informational checklists to help organise documents before a consultation. Requirements differ according to
            the case and the applicable procedure; these lists are general preparation aids only.
          </p>
        </Reveal>

        <div className="mt-14 grid gap-px bg-navy/10 md:grid-cols-2">
          {CHECKLISTS.map((c, i) => (
            <div key={c.id} className="bg-ivory">
              <Reveal delay={(i % 2) * 0.08} className="flex h-full flex-col p-8 sm:p-10">
                <div className="flex items-start justify-between gap-6">
                  <h2 className="font-serif text-2xl font-medium leading-snug text-navy">{c.title}</h2>
                  <span className="font-mono text-xs tracking-[0.2em] text-gold">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <ul className="mt-6 flex-1 space-y-2.5">
                  {c.items.map((it) => (
                    <li key={it} className="flex items-start gap-3 text-sm text-navy/60">
                      <span className="mt-2 h-px w-4 shrink-0 bg-gold" />
                      {it}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`checklist-download-${c.id}`}
                  onClick={() => downloadChecklist(c)}
                  className="btn-outline-dark mt-8 self-start !px-5 !py-2.5"
                >
                  Download Checklist <Download size={12} />
                </button>
              </Reveal>
            </div>
          ))}
        </div>

        <Reveal className="mt-14 border border-navy/15 bg-ivory p-8 sm:p-10">
          <p className="font-mono text-[11px] leading-relaxed tracking-[0.06em] text-slate">
            Every checklist is general information. Document and procedural requirements differ according to the case and
            applicable procedure. These resources do not provide automated legal conclusions and are not a substitute for
            a consultation based on the documents of a particular matter.
          </p>
        </Reveal>
      </div>
    </div>
  );
}
